import { useEffect } from "react";
import Select, { type GroupBase } from "react-select";
import { ColoredLabel, HStack } from "widgets";

import type { InputTableQuerySpec } from "@/graphql.ts";
import { createUniqueKeyFromTableSpec, sameTableSpec } from "@/redux/modules/table-data.ts";
import { Addon } from "@/ui-modules/core/redux-form/Addon.js";

export interface IFeatureOption {
  label: string;
  color: string;
  showColorBadge: boolean;
  tableQuerySpec: InputTableQuerySpec;
}

interface IGenomicFeatureSelectorProps {
  options: IFeatureOption[];
  lastOpenFeatureTableSpec: InputTableQuerySpec;
  setLastOpenFeatureTableSpec: (tableSpec: InputTableQuerySpec) => void;
  groupOptions?: (optionList: IFeatureOption[]) => GroupBase<IFeatureOption>[];
}

const GenomicFeatureSelector = (props: IGenomicFeatureSelectorProps) => {
  useEffect(() => {
    if (props.options?.length) {
      // if the selected option does not fit anymore the genome (it may have changed and may not have this feature type), use the first option
      const selectedOption: IFeatureOption =
        props.options.find((o: IFeatureOption) => sameTableSpec(o.tableQuerySpec, props.lastOpenFeatureTableSpec)) ||
        props.options[0];

      // make sure that the lastOpenFeatureTableSpec is always set to something useful
      props.setLastOpenFeatureTableSpec(selectedOption.tableQuerySpec);
    }
  }, [props.options, props.lastOpenFeatureTableSpec]);

  function genomicFeatureOptionRenderer(featureOption: IFeatureOption) {
    return (
      <>
        {featureOption.showColorBadge ? (
          <ColoredLabel color={featureOption.color}>{featureOption.label}</ColoredLabel>
        ) : (
          <>{featureOption.label}</>
        )}
      </>
    );
  }

  const selectedOption: IFeatureOption = props.options?.find((o: IFeatureOption) =>
    sameTableSpec(o.tableQuerySpec, props.lastOpenFeatureTableSpec),
  );

  return (
    <HStack>
      <Addon label={"Type"} />
      <div style={{ width: 300 }}>
        <Select
          name="filterByGenomicFeatureType"
          placeholder={"select genomic feature type"}
          className={"react-select"}
          classNamePrefix={"react-select"}
          isDisabled={false}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 650,
            }),
          }}
          isClearable={false}
          isSearchable={true}
          // Grouping is required for nucleotide features/markers (in the context of genomes), but not for protein features
          options={props.groupOptions ? props.groupOptions(props.options) : props.options}
          getOptionValue={(option: IFeatureOption) => createUniqueKeyFromTableSpec(option.tableQuerySpec)}
          formatOptionLabel={genomicFeatureOptionRenderer}
          onChange={(selectedOption: IFeatureOption) =>
            props.setLastOpenFeatureTableSpec(selectedOption.tableQuerySpec)
          }
          value={selectedOption}
          noOptionsMessage={() => "No matching type..."}
        />
      </div>
    </HStack>
  );
};

export default GenomicFeatureSelector;
